/**
 * Debug Trace Utility for LLM-Friendly Codebase Instrumentation
 *
 * Usage:
 *   debug.trace('module:action', { trigger: 'click', state: 'open' })
 *
 * Enable debugging:
 *   - Set window.__DEBUG__ = true
 *   - Add ?debug=1 to URL
 *   - Call debug.enable()
 *
 * Anchor naming convention:
 *   module:action - e.g., 'musicPlayer:play', 'cart:add', 'form:submit'
 */
(function(window) {
  'use strict';

  var DEBUG_KEY = '__DEBUG__';
  var TRACE_PREFIX = '[TRACE]';
  var enabled = null; // Lazy evaluation
  var hooks = [];

  /**
   * Check if debugging is enabled
   */
  function isEnabled() {
    if (enabled !== null) return enabled;

    // Check window flag
    if (window[DEBUG_KEY] === true) {
      enabled = true;
      return true;
    }

    // Check URL param
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('debug') === '1' || params.get('debug') === 'true') {
        enabled = true;
        return true;
      }
    } catch (e) {
      // URLSearchParams not supported, try manual parse
      if (window.location.search.indexOf('debug=1') !== -1 ||
          window.location.search.indexOf('debug=true') !== -1) {
        enabled = true;
        return true;
      }
    }

    // Check localStorage for persistent debug mode
    try {
      if (window.localStorage && window.localStorage.getItem('debug') === '1') {
        enabled = true;
        return true;
      }
    } catch (e) {
      // localStorage not available
    }

    enabled = false;
    return false;
  }

  /**
   * Core trace function
   * @param {string} anchor - Semantic anchor in format 'module:action'
   * @param {object} [data] - Optional context data
   */
  function trace(anchor, data) {
    if (!isEnabled()) return;

    var timestamp = new Date().toISOString();
    var logData = {
      anchor: anchor,
      time: timestamp,
      data: data || {}
    };

    // Console output
    if (data && Object.keys(data).length > 0) {
      console.log(TRACE_PREFIX, anchor, data);
    } else {
      console.log(TRACE_PREFIX, anchor);
    }

    // Execute registered hooks (for analytics, monitoring, etc.)
    for (var i = 0; i < hooks.length; i++) {
      try {
        hooks[i](anchor, data, timestamp);
      } catch (e) {
        console.warn('[DEBUG] Hook error:', e);
      }
    }

    return logData;
  }

  /**
   * Register a hook for trace events
   * @param {function} fn - Callback function(anchor, data, timestamp)
   */
  function addHook(fn) {
    if (typeof fn === 'function') {
      hooks.push(fn);
    }
  }

  /**
   * Remove a registered hook
   * @param {function} fn - The hook function to remove
   */
  function removeHook(fn) {
    var idx = hooks.indexOf(fn);
    if (idx !== -1) {
      hooks.splice(idx, 1);
    }
  }

  /**
   * Enable debugging programmatically
   */
  function enable() {
    enabled = true;
    window[DEBUG_KEY] = true;
    try {
      if (window.localStorage) {
        window.localStorage.setItem('debug', '1');
      }
    } catch (e) {}
    console.log(TRACE_PREFIX, 'debug:enabled', { method: 'manual' });
  }

  /**
   * Disable debugging
   */
  function disable() {
    enabled = false;
    window[DEBUG_KEY] = false;
    try {
      if (window.localStorage) {
        window.localStorage.removeItem('debug');
      }
    } catch (e) {}
  }

  /**
   * Get all trace anchors found in page (for discovery)
   * Searches for debug.trace calls in inline scripts
   */
  function discoverAnchors() {
    var anchors = [];
    var scripts = document.querySelectorAll('script:not([src])');
    var regex = /debug\.trace\s*\(\s*['"]([^'"]+)['"]/g;

    for (var i = 0; i < scripts.length; i++) {
      var content = scripts[i].textContent;
      var match;
      while ((match = regex.exec(content)) !== null) {
        if (anchors.indexOf(match[1]) === -1) {
          anchors.push(match[1]);
        }
      }
    }

    return anchors;
  }

  // Export to window.debug namespace
  window.debug = window.debug || {};
  window.debug.trace = trace;
  window.debug.addHook = addHook;
  window.debug.removeHook = removeHook;
  window.debug.enable = enable;
  window.debug.disable = disable;
  window.debug.isEnabled = isEnabled;
  window.debug.discoverAnchors = discoverAnchors;

  // Auto-announce if debug mode is active
  if (isEnabled()) {
    console.log(TRACE_PREFIX, 'debug:init', {
      version: '1.0.0',
      enabled: true,
      hint: 'Use debug.discoverAnchors() to find all trace points'
    });
  }

})(window);
